import http from '@/utils/httpClient';
import { toaster } from '@/utils/toast';
import { Action, Thunk, action, thunk } from 'easy-peasy';
import { Timestamp } from 'firebase/firestore';

export interface totalUser {
  unknownUserCount: number;
  providerCount: number;
  advisorCount: number;
  total: number;
}

// export interface jobValue{
//     date: string,
//     budget: string
// }
export interface result {
  date: string;
  budget: string;
  hiredCandidateData: string;
}

export interface dashboardGrowthChart {
  hiredTotalBudget: number;
  totalBudget: number;
  // jobValue: jobValue[],
  // hiredCandidates: jobValue[],
  result: result[];
}

export interface userGrowthValue {
  month: string;
  totalRequests: string;
}

export interface userGrowthChart {
  providerList: userGrowthValue[];
  advisorList: userGrowthValue[];
}

export interface businessGrowth {
  dashboardGrowthChart: dashboardGrowthChart;
}

export interface userGrowth {
  dashboardGrowthChart: userGrowthChart;
}

export interface approveRequestPayload {
  id: string;
  status: string;
}

export interface selectedUser {
  id: string;
}

export interface RequestDialog {
  open: boolean;
  type: string;
}
export interface manageUsersParams {
  id: string;
  request_type: 'suspend' | 'delete';
}

export interface profilePic {
  data: [];
  type: string;
}

export interface usersData {
  userId: string;
  requestStatusId: string;
  userTypeId: string;
  firstName: string;
  lastName: string;
  emailId: string;
  type: string;
  profilePic: profilePic;
  phone: string;
  dateAdded: Date;
  tosAcceptedAt: string | null;
  tosAcceptedVersion: string | null;
}

export interface newEnrolledUsers {
  users: usersData[] | undefined;
}

export interface pendingUsers {
  users: usersData[] | undefined;
}

export interface allUsers {
  users: usersData[] | undefined;
}

export interface roomView {
  roomID: string;
  C1_first_name: string;
  C2_first_name: string;
  C1_photo_id: string;
  C2_photo_id: string;
  c1_id: string;
  c2_id: string;
}

export interface recentChatView {
  C1_first_name: string;
  C1_Id: string;
  C2_first_name: string;
  C2_Id: string;
  C1_photo_id: string;
  C2_photo_id: string;
  last_msg: string;
  roomID: string;
  time: Timestamp;
}

export interface adminChatData {
  message: string;
  s_id: string;
  img_val: string;
  time: Timestamp;
}

export interface adminUser {
  id: string;
  firstName: string;
  lastName: string;
  emailId: string;
}

export interface inviteAdminPayload {
  first_name: string;
  last_name: string;
  email: string;
}

export interface IAdminModel {
  totalUser: totalUser;
  newEnrolledUsers: newEnrolledUsers | undefined;
  allUsers: allUsers | undefined;
  pendingUsers: pendingUsers | undefined;
  requestDialog: RequestDialog;
  selectedUser: string;
  businessGrowth: businessGrowth | undefined;
  userGrowth: userGrowth | undefined;
  manageUsersParams: manageUsersParams | undefined;
  recentChatView: recentChatView[];
  activeRoom: roomView | undefined;
  adminChatData: adminChatData[];
  activePage: number;
  menuSubItem: number;
  admins: adminUser[] | undefined;

  /*-------- ACTION TYPE ----------*/
  setTotalUser: Action<IAdminModel, totalUser>;
  setPendingUsers: Action<IAdminModel, pendingUsers>;
  setAllUsers: Action<IAdminModel, allUsers>;
  setNewEnrolledUsers: Action<IAdminModel, newEnrolledUsers>;
  setRequestDialog: Action<IAdminModel, RequestDialog>;
  setSelectedUser: Action<IAdminModel, string>;
  setBusinessGrowth: Action<IAdminModel, businessGrowth>;
  setUserGrowth: Action<IAdminModel, userGrowth>;
  setDeleteOrSuspendUsers: Action<IAdminModel, manageUsersParams>;
  setRecentChatView: Action<IAdminModel, recentChatView[]>;
  setActiveRoom: Action<IAdminModel, roomView>;
  setAdminChatData: Action<IAdminModel, adminChatData[]>;
  setActivePage: Action<IAdminModel, number>;
  setMenuSubItem: Action<IAdminModel, number>;
  setAdmins: Action<IAdminModel, adminUser[]>;

  /*-------- THUNK TYPE -----------*/
  getTotalUser: Thunk<IAdminModel>;
  getNewEnrolledUsers: Thunk<IAdminModel>;
  getAllUsers: Thunk<IAdminModel, string>;
  getPendingUsers: Thunk<IAdminModel>;
  approveUserRequest: Thunk<IAdminModel, approveRequestPayload>;
  getBusinessGrowth: Thunk<IAdminModel, string>;
  getUserGrowth: Thunk<IAdminModel>;
  getDeleteOrSuspendUsers: Thunk<IAdminModel, manageUsersParams>;
  getAdmins: Thunk<IAdminModel>;
  inviteAdmin: Thunk<IAdminModel, inviteAdminPayload, undefined, object, Promise<boolean>>;
}

const AdminModel: IAdminModel = {
  totalUser: { unknownUserCount: 0, providerCount: 0, advisorCount: 0, total: 0 },
  newEnrolledUsers: undefined,
  allUsers: undefined,
  pendingUsers: undefined,
  requestDialog: { open: false, type: '' },
  selectedUser: '',
  businessGrowth: undefined,
  userGrowth: undefined,
  manageUsersParams: undefined,
  recentChatView: [],
  activeRoom: undefined,
  adminChatData: [],
  activePage: 0,
  menuSubItem: 0,
  admins: undefined,

  /*------- ACTION TYPE -------*/
  setTotalUser: action((state, value) => {
    state.totalUser = value;
  }),
  setPendingUsers: action((state, value) => {
    state.pendingUsers = value;
  }),
  setAllUsers: action((state, value) => {
    state.allUsers = value;
  }),
  setNewEnrolledUsers: action((state, value) => {
    state.newEnrolledUsers = value;
  }),
  setRequestDialog: action((state, value) => {
    state.requestDialog = value;
  }),
  setSelectedUser: action((state, value) => {
    state.selectedUser = value;
  }),
  setBusinessGrowth: action((state, value) => {
    state.businessGrowth = value;
  }),
  setUserGrowth: action((state, value) => {
    state.userGrowth = value;
  }),
  setDeleteOrSuspendUsers: action((state, value) => {
    state.manageUsersParams = value;
  }),
  setRecentChatView: action((state, value) => {
    state.recentChatView = value;
  }),
  setActiveRoom: action((state, value) => {
    state.activeRoom = value;
  }),
  setAdminChatData: action((state, value) => {
    state.adminChatData = value;
  }),
  setActivePage: action((state, value) => {
    state.activePage = value;
  }),
  setMenuSubItem: action((state, value) => {
    state.menuSubItem = value;
  }),
  setAdmins: action((state, value) => {
    state.admins = value;
  }),

  /*------- THUNK TYPE -------*/
  getTotalUser: thunk(async (action) => {
    await http()
      .get('/admin/total-user')
      .then((res) => {
        action.setTotalUser(res.data.totalUser);
      })
      .catch(() => toaster('error', 'Error in fetching user values'));
  }),

  getNewEnrolledUsers: thunk(async (action) => {
    await http()
      .get(`/admin/users/?status=verified`)
      .then((res) => {
        action.setNewEnrolledUsers(res.data);
      })
      .catch(() => toaster('error', 'Error in fetching enrolled users'));
  }),

  getAllUsers: thunk(async (action, payload) => {
    await http()
      .get(`/admin/users/?status=all&search=${payload}`)
      .then((res) => {
        action.setAllUsers(res.data);
      })
      .catch(() => toaster('error', 'Error in fetching users'));
  }),

  getPendingUsers: thunk(async (action) => {
    await http()
      .get(`/admin/users/?status=pending`)
      .then((res) => {
        action.setPendingUsers(res.data);
      })
      .catch(() => toaster('error', 'Error in fetching enrolled users'));
  }),

  approveUserRequest: thunk(async (_action, payload) => {
    await http()
      .put(`/admin/user/${payload.id}/approval/${payload.status}`)
      .then(() => {
        toaster('success', payload.status === 'verified' ? 'User approved' : 'User rejected');
      })
      .catch(() => {
        toaster('error', 'Error in Approving request');
      });
  }),

  getBusinessGrowth: thunk(async (action, payload) => {
    await http()
      .get(`/admin/business-growth?graph=${payload}`)
      .then((res) => {
        action.setBusinessGrowth(res.data);
      })
      .catch(() => toaster('error', 'Error in fetching business growth'));
  }),

  getUserGrowth: thunk(async (action) => {
    await http()
      .get('/admin/count')
      .then((res) => {
        action.setUserGrowth(res.data);
      })
      .catch(() => {
        toaster('error', 'Error in fetching user growth info.');
      });
  }),

  getDeleteOrSuspendUsers: thunk(async (action, payload) => {
    await http()
      .put(`/admin/user/manage?request_type=${payload.request_type}&id=${payload.id}`)
      .then((res) => {
        action.setDeleteOrSuspendUsers(res.data);
        toaster('success', res.data.message);
      })
      .catch(() => toaster('error', 'Error occurred while trying to delete or suspend the user.'));
  }),

  getAdmins: thunk(async (action) => {
    await http()
      .get('/admin/admins')
      .then((res) => {
        action.setAdmins(res.data.admins);
      })
      .catch(() => toaster('error', 'Error in fetching admins'));
  }),

  inviteAdmin: thunk(async (action, payload) => {
    try {
      const res = await http().post('/admin/invite', payload);
      toaster('success', res.data?.message || 'Admin invited successfully');
      await action.getAdmins();
      return true;
    } catch (e: any) {
      const message = e?.response?.data?.error || 'Failed to invite admin';
      toaster('error', message);
      return false;
    }
  }),
};

export default AdminModel;
